<script lang="ts">
    import Component, { mixins } from 'vue-class-component';
    import AlternativeEditor from "@Alternatives/Alternatives/Components/AlternativeEditor.vue";
    import { AlternativeEditorDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeEditorDialog';
    import AlternativesCommands from "@Alternatives/Alternatives/Domain/commands-vue-component";
    import { CreateAlternativeCommandV1, DeleteAlternativeCommandV1, UpdateAlternativeCommandV1 } from '@Alternatives/Alternatives/Domain/commands-core';
    import AlternativesQueries from "@Alternatives/Alternatives/Domain/queries-vue-component";
    import { AlternativeEditorReadModel } from "@Alternatives/Alternatives/Domain/readmodels";
    import { AlternativeAttributeValueDTO } from "@Alternatives/Model";
    import { ICommandBase } from '@Alternatives/cqrs';
    import { EditorState } from "@common/Common";
    import { guid } from "@common/Guid";
    import { ValidationError } from '@common/types';


    @Component({
        components: { AlternativeEditor },
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        },
        watch: {
            opened: 'onOpenedChanged'
        }
    })
    export default class AlternativeEditorDialog extends mixins(AlternativesQueries, AlternativesCommands)
    {
        get vm(): AlternativeEditorDialogViewModel { return this.$props.viewModel as AlternativeEditorDialogViewModel; }
        
        get opened(): boolean { return this.vm.state !== EditorState.Closed; }
        get title(): string { return this.vm.title; }
        get rm(): AlternativeEditorReadModel { return this.vm.rm; }
        get errors(): ValidationError[] { return this.vm.validationErrors; }

        get isDeleting(): boolean { return this.vm.isDeleting; }
        get isDeleteVisible() { return this.vm.state === EditorState.Editor; }
        get operationInProgress(): boolean { return this.vm.isCreating === true || this.vm.isUpdating === true || this.isDeleting === true; }
        get deleteButtonLabel(): string { return this.isDeleting === false ? 'Delete' : 'Deleting...'; }
        get saveButtonLabel(): string { return this.vm.isCreating === false && this.vm.isUpdating === false ? 'Save' : 'Saving...'; }
        
        get userId(): number | null { return this.rm.UserId; }
        

        //@Watch('opened')
        onOpenedChanged(val: boolean, oldVal: boolean)
        {
            if (val === true)
            {
                 if (this.vm.state === EditorState.Creator)
                 {
                    this.runAlternativeEditorCreationQueryV1(this.vm.workspaceId, this.vm.projectId, this.vm.alternativesListId, this.vm.formDefId);
                 }
                 else if (this.vm.state === EditorState.Editor)
                 {
                      this.runAlternativeEditorEditionQueryV1(this.vm.workspaceId, this.vm.projectId, this.vm.alternativesListId, this.vm.alternativeId, this.vm.formDefId);
                 }
                 else if (this.vm.state === EditorState.Preview)
                 {
                     this.runAlternativeEditorPreviewQueryV1(this.vm.workspaceId, this.vm.projectId, this.vm.alternativesListId, this.vm.formDefId);
                 }   
            }
        }

        close()
        {
            this.vm.close();
        }

        save(name: string, description: string, statusId: number, attributeValues: AlternativeAttributeValueDTO[])
        {
            if (this.vm.state === EditorState.Creator)
            {
                this.handleCreateAlternativeCommand(name, description, statusId, attributeValues);
            }
            else if (this.vm.state === EditorState.Editor)
            {
                this.handleUpdateAlternativeCommand(name, description, statusId, attributeValues);
            }
        }

        handleCreateAlternativeCommand(name: string, description: string, statusId: number, attributeValues: AlternativeAttributeValueDTO[])
        {
            this.vm.isCreating = true;
            this.invokeCreateAlternativeCommandV1(this.userId, this.vm.workspaceId, this.vm.projectId, this.vm.alternativesListId, this.vm.formDefId, guid(), name, description, statusId, attributeValues);
        }

        handleUpdateAlternativeCommand(name: string, description: string, statusId: number, attributeValues: AlternativeAttributeValueDTO[])
        {
            this.vm.isUpdating = true;
            this.invokeUpdateAlternativeCommandV1(this.userId, this.vm.workspaceId, this.vm.projectId, this.vm.alternativesListId, this.vm.formDefId, this.vm.alternativeId, name, description, statusId, attributeValues);
        }

        handleDeleteAlternativeCommand()
        {
            this.vm.isDeleting = true;
            this.invokeDeleteAlternativeCommandV1(this.userId, this.vm.workspaceId, this.vm.projectId, this.vm.alternativesListId, this.vm.alternativeId);
        }

        onSetModel(model: any, modelName: string) 
        {
            if (modelName == "AlternativeEditorReadModel")
            {
                this.vm.rm = model as AlternativeEditorReadModel;
            }
        }

        onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
        {
            if (DeleteAlternativeCommandV1.matchType(cmd)) 
            {
                this.close();
            }
        }

        onCommandFailed(status: any)
        {
            if (CreateAlternativeCommandV1.matchType(status.Command))
            {
                this.vm.DisplayValidationErrors(status.ValidationErrors);
            }
            else if (UpdateAlternativeCommandV1.matchType(status.Command))
            {
                this.vm.DisplayValidationErrors(status.ValidationErrors);
            }
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened === true">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">

                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <AlternativeEditor v-if="rm" :propRm="rm" :propErrors="errors" propFormId="alternativeEditorForm"
                                            @form-submit="save" />
                    </div>
                    <div class="modal-footer">
                        <div class="w-100 text-right">
                            <div class="dropup float-left">
                                <button v-if="isDeleteVisible" :disabled="operationInProgress === true" type="button" class="btn btn-danger" data-toggle="dropdown">
                                    {{ deleteButtonLabel }}
                                </button>
                                <div class="dropdown-menu">
                                    <div class="btn-group">
                                        <button type="button" class="btn btn-outline-secondary">Cancel</button>
                                        <button @click="handleDeleteAlternativeCommand" type="button" class="btn btn-danger">Confirm</button>
                                    </div>
                                </div>
                            </div>

                            <button type="submit" form="alternativeEditorForm" class="btn btn-primary" :disabled="operationInProgress === true">
                                {{ saveButtonLabel }}
                            </button>

                            <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>